<template>
  <div class="catalog-page">
    <header class="header">
      <div class="header-content">
        <div class="logo">
          <img src="@/assets/images/logcomex-logo.svg" alt="Logcomex">
        </div>
        <a href="https://conteudo.logcomex.com/catalogo-de-produtos" 
           target="_blank" 
           class="demo-button">
          Agende agora uma demonstração
        </a>
      </div>
    </header>

    <main class="main-content">
      <div class="hero-section">
        <div class="tag">Logcomex + Inteligência Artificial </div>
        
        <h1 class="title">
          Descubra o poder da IA com um simples upload de documento: seu Catálogo de Produtos em segundos!
        </h1>
        
        <p class="subtitle">  
          Faça o upload do XML da Nota Fiscal ou da DI e veja a IA preencher seus atributos automaticamente e de forma gratuita.
        </p>

        <!-- Initial Upload State -->
        <div v-if="currentState === 'upload'"
             class="upload-section" 
             @dragover.prevent="handleDragOver"
             @dragleave.prevent="handleDragLeave"
             @drop.prevent="handleDrop"
             :class="{ 'dragging': isDragging }">
          <div class="upload-content">
            <div class="upload-icon">
              <font-awesome-icon icon="fa-solid fa-cloud-upload" size="3x" />
            </div>
            <p class="upload-text">Arraste e solte um ou mais documentos aqui (máximo de 10 arquivos)</p>
            <p class="upload-separator">ou</p>
            <input 
              type="file" 
              ref="fileInput" 
              @change="handleFileSelect" 
              multiple 
              accept=".xml"
              class="file-input"
            >
            <button class="select-files-button" @click="triggerFileInput">
              <font-awesome-icon icon="fa-solid fa-paperclip" class="paperclip-icon"/> Selecione o(s) arquivo(s)
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="currentState === 'loading'" class="loading-section">
          <p class="loading-text">Analisando seus produtos... Preenchendo seus atributos</p>
          <div class="loading-bar"></div>
        </div>

        <!-- Success State -->
        <div v-if="currentState === 'success'" class="success-section">
          <div class="success-info">
            <h2 class="success-title">
              Seu catálogo de produtos DUIMP com atributos preenchidos automaticamente está pronto para download. Baixe o JSON agora mesmo!
            </h2>
            <div class="success-stats">
              <div class="stat-item">
                <svg class="stat-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#6B21A8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ productsFound }} produtos identificados</span>
              </div>
              <div class="stat-item">
                <svg class="stat-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#6B21A8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ ncmsFound }} NCM's distintas</span>
              </div>
            </div>
            <p class="success-note">
              Todos os produtos encontrados foram padronizados no arquivo de Catálogo de Produtos DUIMP.
            </p>
          </div>

          <div class="email-form">
            <h3 class="email-title">
              Seu arquivo será enviado para o e-mail informado com as instruções para importação no catálogo de produtos do Portal Único Siscomex.
            </h3>
            <div class="email-input-container">
              <input 
                type="email" 
                v-model="email" 
                @input="handleEmailInput"
                placeholder="Informe seu e-mail"
                class="email-input"
                :class="{ 'error': emailError }"
              >
              <span class="email-error" v-if="emailError">{{ emailError }}</span>
            </div>
            <button 
              class="email-button" 
              @click="handleEmailSubmit" 
              :disabled="!isValidEmail || isSubmitting"
            >
              <font-awesome-icon 
                v-if="!isSubmitting" 
                icon="fa-solid fa-envelope" 
                class="button-icon"
              />
              <span v-if="isSubmitting">Enviando...</span>
              <span v-else>Receber download por e-mail</span>
            </button>
          </div>
        </div>

        <!-- Add new submitted state -->
        <div v-if="currentState === 'submitted'" class="submitted-section">
          <div class="submitted-content">
            <h2 class="submitted-title">
              Estamos processando os seus arquivos e enviaremos para o seu e-mail o arquivo JSON contendo seus produtos com os atributos preenchidos.
            </h2>
            <button @click="resetToUpload" class="reset-button">
              <font-awesome-icon icon="fa-solid fa-upload" class="button-icon" />
              Enviar novos arquivos
            </button>
          </div>
        </div>
      </div>

      <section class="benefits-section">
        <h2 class="benefits-title">
          Aumente seu impacto e receita<br>
          com o Catálogo de Produtos Logcomex
        </h2>

        <div class="benefits-content">
          <div class="benefits-images">
            <div class="purple-line"></div>
            <div class="image-group">
              <div class="image-1-container">
                <img src="@/assets/images/image_1.png" alt="Importação" class="image-1">
              </div>
              <div class="text-overlay">
                <h3>Garantindo agilidade<br>nas suas importações<br>através da<br>DUIMP</h3>
              </div>
              <div class="image-2-container">
                <img src="@/assets/images/image_2.png" alt="Análise" class="image-2">
              </div>
            </div>
          </div>

          <div class="benefits-list">
            <div class="benefit-item">
              <div class="number">1.</div>
              <div class="benefit-text">
                <h4>Aderir</h4>
                <p>Contrate o Catálogo de Produtos Logcomex e comece a transformar sua gestão de importações com uma solução automatizada e eficiente.</p>
              </div>
            </div>

            <div class="benefit-item">
              <div class="number">2.</div>
              <div class="benefit-text">      
                <h4>Vender</h4>
                <p>Ofereça o serviço de gestão de catálogos para seus clientes, facilitando o processo de importação e agregando valor ao seu portfólio.</p>
              </div>
            </div>

            <div class="benefit-item">
              <div class="number">3.</div>
              <div class="benefit-text">
                <h4>Lucrar</h4>
                <p>Amplie suas oportunidades de receita, ganhando comissões ao ajudar seus clientes a economizar tempo e reduzir erros nas operações de importação.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { getApiUrl } from '@/config'

const isDragging = ref(false)
const fileInput = ref(null)
const currentState = ref('upload') // 'upload', 'loading', 'success', 'submitted'
const email = ref('')
const emailError = ref('')
const productsFound = ref(0)
const ncmsFound = ref(0)
const isSubmitting = ref(false)
const uploadedFiles = ref([]) // Store the uploaded files
const MAX_FILES_LIMIT = 10

const isValidEmail = computed(() => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/
  return emailRegex.test(email.value)
})

const validateEmail = () => {
  if (!email.value) {
    emailError.value = 'Por favor, informe seu e-mail'
    return false
  }
  if (!isValidEmail.value) {
    emailError.value = 'Por favor, informe um e-mail válido'
    return false
  }
  emailError.value = ''
  return true
}

const handleEmailInput = () => {
  if (email.value) {
    validateEmail()
  } else {
    emailError.value = ''
  }
}

const handleDragOver = (event) => {
  isDragging.value = true
  event.preventDefault()
}

const handleDragLeave = (event) => {
  isDragging.value = false
  event.preventDefault()
}

const validateXMLFiles = (files) => {
  const xmlFiles = files.filter(file => file.name.toLowerCase().endsWith('.xml'))
  const invalidFiles = files.filter(file => !file.name.toLowerCase().endsWith('.xml'))
  
  if (invalidFiles.length > 0) {
    const invalidFileNames = invalidFiles.map(file => file.name).join(', ')
    alert(`Os seguintes arquivos não são XML e foram ignorados:\n${invalidFileNames}\n\nPor favor, selecione apenas arquivos XML.`)
  }

  if (xmlFiles.length === 0) {
    alert('Nenhum arquivo XML foi selecionado. Por favor, selecione pelo menos um arquivo XML.')
    return null
  }

  if (xmlFiles.length > MAX_FILES_LIMIT) {
    alert(`Você pode enviar no máximo ${MAX_FILES_LIMIT} arquivos por vez. Você selecionou ${xmlFiles.length} arquivos. Por favor, selecione no máximo ${MAX_FILES_LIMIT} arquivos.`)
    return null
  }

  return xmlFiles
}

const handleDrop = (event) => {
  isDragging.value = false
  event.preventDefault()
  
  const files = Array.from(event.dataTransfer.files)
  const validFiles = validateXMLFiles(files)
  
  if (validFiles) {
    handleFiles(validFiles)
  }
}

const triggerFileInput = () => {
  fileInput.value.click()
}

const handleFileSelect = (event) => {
  const files = Array.from(event.target.files)
  const validFiles = validateXMLFiles(files)
  
  if (validFiles) {
    handleFiles(validFiles)
  }
  
  // Reset file input to allow selecting the same file again
  event.target.value = ''
}

const handleFiles = async (files) => {
  try {
    // Store the files for later use
    uploadedFiles.value = files
    
    // Change to loading state
    currentState.value = 'loading'

    // Create FormData and append files
    const formData = new FormData()
    files.forEach(file => {
      formData.append('files', file)
    })

    // Call the API endpoint using the configuration
    const response = await fetch(getApiUrl('/extract-attribute/files/validate'), {
      method: 'POST',
      body: formData,
      credentials: 'omit',
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-API-Key': process.env.VUE_APP_X_API_KEY
      }
    })

    const result = await response.json()

    if (!result.success) {
      throw new Error(result.error || 'Erro ao processar os arquivos')
    }

    // Store the results for display
    productsFound.value = result.data.total_products
    ncmsFound.value = result.data.total_ncms

    // Change to success state
    currentState.value = 'success'
  } catch (error) {
    console.error('Error processing files:', error)
    alert('Ocorreu um erro ao processar os arquivos. Por favor, tente novamente.')
    currentState.value = 'upload'
  }
}

const handleEmailSubmit = async () => {
  if (!validateEmail()) {
    return
  }

  if (uploadedFiles.value.length === 0) {
    alert('Nenhum arquivo foi carregado. Por favor, faça o upload de pelo menos um arquivo XML.')
    return
  }

  try {
    isSubmitting.value = true
    
    // Create FormData and append email and files
    const formData = new FormData()
    formData.append('email', email.value)
    
    uploadedFiles.value.forEach(file => {
      formData.append('files', file)
    })

    // Call the API endpoint to process files and send email
    const response = await fetch(getApiUrl('/extract-attribute/files'), {
      method: 'POST',
      body: formData,
      credentials: 'omit',
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-API-Key': process.env.VUE_APP_X_API_KEY
      }
    })

    const result = await response.json()

    if (result.status === 'failed') {
      throw new Error(result.error || 'Erro ao enviar os arquivos')
    }

    // Change to submitted state
    currentState.value = 'submitted'
    
  } catch (error) {
    console.error('Error sending files:', error)
    alert('Ocorreu um erro ao enviar os arquivos. Por favor, tente novamente.')
  } finally {
    isSubmitting.value = false
  }
}

const resetToUpload = () => {
  currentState.value = 'upload'
  email.value = ''
  uploadedFiles.value = []
  emailError.value = ''
}
</script>

<style scoped>
.catalog-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #F5F0FF 0%, #F0F7FF 100%);
}

.header {
  padding: 10px 40px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
}

.logo img {
  height: 30px;
}

.main-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0px 20px 40px 20px;
}

/* Hero Section Styles */
.hero-section {
  text-align: center;
  margin-bottom: 40px;
  position: relative;
  padding: 40px 20px;
  background-image: url('@/assets/images/arrow.png');
  background-repeat: no-repeat;
  background-position: 90% center;
  background-size: 20% auto;
}

.tag {
  display: inline-block;
  background: rgba(107, 33, 168, 0.1);
  color: #6B21A8;
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 24px;
}

.title {
  font-size: 42px;
  color: #1E293B;
  margin: 0 auto 24px;
  line-height: 1.2;
}

.subtitle {
  font-size: 18px;
  color: #64748B;
  max-width: 700px;
  margin: 0 auto 48px;
  line-height: 1.5;
}

.upload-section {
  background: white;
  border-radius: 24px;
  padding: 48px;
  max-width: 710px;
  margin: 0 auto;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.upload-content {
  background: linear-gradient(135deg, rgba(245, 240, 255, 0.5) 0%, rgba(240, 247, 255, 0.5) 100%);
  border: 2px dashed rgba(107, 33, 168, 0.2);
  border-radius: 16px;
  padding: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.upload-section.dragging .upload-content {
  border-color: #6B21A8;
  background: linear-gradient(135deg, rgba(245, 240, 255, 0.8) 0%, rgba(240, 247, 255, 0.8) 100%);
}

.upload-icon {
  color: #6B21A8;
  margin-bottom: 8px;
  font-size: 12px;
}

.upload-text {
  color: #64748B;
  font-size: 16px;
  margin: 0;
}

.upload-separator {
  color: #94A3B8;
  font-size: 14px;
  margin: 0;
}

.file-input {
  display: none;
}

.select-files-button {
  background: #F3E8FF;
  color: #6B21A8;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.select-files-button:hover {
  background: #E9D5FF;
}

/* Loading State */
.loading-section {
  background: white;
  border-radius: 16px;
  padding: 48px;
  max-width: 600px;
  margin: 0 auto;
}

.loading-text {
  color: #1E293B;
  font-size: 18px;
  margin-bottom: 24px;
}

.loading-bar {
  width: 100%;
  height: 4px;
  background: #E2E8F0;
  border-radius: 2px;
  overflow: hidden;
  position: relative;
}

.loading-bar::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 30%;
  background: #6B21A8;
  animation: loading 1.5s infinite;
  border-radius: 2px;
}

@keyframes loading {
  0% { left: -30%; }
  100% { left: 100%; }
}

/* Success State */
.success-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  background: white;
  border-radius: 24px;
  padding: 40px;
  max-width: 1000px;
  margin: 0 auto;
  text-align: left;
}

.success-info {
  padding: 20px;
}

.success-title {
  font-size: 32px;
  color: #1E293B;
  margin-bottom: 24px;
}

.success-stats {
  margin-bottom: 24px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  color: #1E293B;
}

.stat-icon {
  color: #6B21A8;
}

.success-note {
  color: #64748B;
  font-size: 14px;
  margin-bottom: 0;
}

.email-form {
  background: #F8FAFC;
  padding: 32px;
  border-radius: 16px;
}

.email-title {
  font-size: 16px;
  color: #1E293B;
  margin-bottom: 16px;
}

.email-input-container {
  position: relative;
  margin-bottom: 26px;
}

.email-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s ease;
}

.email-input.error {
  border-color: #DC2626;
}

.email-input:focus {
  outline: none;
  border-color: #6B21A8;
  box-shadow: 0 0 0 2px rgba(107, 33, 168, 0.1);
}

.email-error {
  position: absolute;
  left: 0;
  bottom: -20px;
  color: #DC2626;
  font-size: 12px;
}

.email-button {
  background: #6B21A8;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
  transition: all 0.2s ease;
}

.email-button:hover:not(:disabled) {
  background: #581C87;
}

.email-button:disabled {
  background: #E2E8F0;
  cursor: not-allowed;
  opacity: 0.7;
}

.button-icon {
  font-size: 16px;
}

/* Benefits Section Styles */
.benefits-section {
  text-align: center;
  margin-top: 40px;
  background: white;
  border-radius: 24px;
  padding: 80px;
}

.benefits-title {
  font-size: 36px;
  color: #0A2B6B;
  margin-bottom: 80px;
  line-height: 1.2;
}

.benefits-content {
  display: grid;
  grid-template-columns: minmax(auto, 600px) 1fr;
  gap: 80px;
  align-items: center;
  margin-top: 60px;
}

.benefits-images {
  position: relative;
  width: 100%;
  height: 500px;
  grid-column: 1;
}

.image-group {
  position: relative;
  width: 100%;
  height: 100%;
}

.image-1-container {
  position: absolute; 
  left: 0;
  top: 0;
  width: 70%;
  height: 70%;
  z-index: 1;
}

.image-2-container {
  position  : absolute;
  right: 26px;
  bottom: 0;
  width: 60%;
  height: 60%;
  z-index: 2;
}

.image-1, .image-2 {
  width: 100%;
  height: 100%; 
  object-fit: cover;
  border-radius: 24px;
}

.text-overlay {
  position: absolute;
  left: 65%;
  top: -10%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 40px;
  border-radius: 24px;
  width: 70%;
  z-index: 3;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.text-overlay h3 {
  color: #0A2B6B;
  font-size: 32px;
  line-height: 1.4;
  font-weight: 600;
  margin: 0;
}

.purple-line {
  position: absolute;
  left: 60px;
  top: 70%;
  transform: translateY(-50%);
  width: 6px;
  height: 72px;
  background: #6B21A8;
  border-radius: 2px;
  z-index: 4;
}

.benefits-list {
  grid-column: 2;
  padding-right: 40px;
}

.benefit-item {
  margin-bottom: 48px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 24px;
}

.number {
  color: #6B21A8;
  font-size: 64px;
  font-weight: 700;
  line-height: 1; 
  flex-shrink: 0;
}

.benefit-text {
  text-align: left;
  padding-top: 12px;
}

.benefit-text h4 {
  color: #6B21A8;
  font-size: 24px;
  margin: 0;
  font-weight: 600;
  text-align: left;
}

.benefit-text p {
  color: #64748B;
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
  text-align: left;
  max-width: 400px;
}

/* Add subtle separator between items */
.benefit-item:not(:last-child) {
  position: relative;
  padding-bottom: 48px;
}

.benefit-item:not(:last-child)::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background: #E2E8F0;
}

.demo-button {
  background-color: #6B21A8;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  font-size: 16px;
  transition: background-color 0.2s ease;
}

.demo-button:hover {
  background-color: #581C87;
}

@media (max-width: 1200px) {
  .header-content {
    display: inline;
  }

  .demo-button {
    padding: 10px 20px;
  }

  .logo {
    padding: 0px 0px 12px 0px;
  }

  .benefits-content {
    grid-template-columns: 1fr;
    gap: 0px;
  }

  .benefits-images {
    height: 400px;
    display: none;
  }

  .benefits-list {
    padding-right: 0;
  }

  .text-overlay {
    width: 80%;
    padding: 30px;
  }

  .text-overlay h3 {
    font-size: 28px;
  }
}

@media (max-width: 768px) {
  .title {
    font-size: 15px;
  }

  .subtitle {
    font-size: 15px;
  }

  .benefits-section {
    padding: 40px 20px;
  }

  .benefits-images {
    height: 300px;
  }

  .text-overlay {
    width: 90%;
    padding: 24px;
  }

  .text-overlay h3 {
    font-size: 24px;
  }

  .benefit-item {
    gap: 16px;
  }

  .number {
    font-size: 48px;
  }
  
  .benefit-text h4 {
    font-size: 20px;
    margin-bottom: 8px;
  }
  
  .benefit-text p {
    font-size: 15px;
  }
}

.paperclip-icon {
  margin-right: 8px;
  font-size: 14px;
}

/* Add new submitted state */
.submitted-section {
  background: white;
  border-radius: 24px;
  padding: 48px;
  max-width: 710px;
  margin: 0 auto;
  text-align: center;
}

.submitted-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
}

.submitted-title {
  font-size: 24px;
  color: #1E293B;
  line-height: 1.4;
  margin: 0;
}

.reset-button {
  background: #6B21A8;
  color: white;
  padding: 16px 32px;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background-color 0.2s ease;
}

.reset-button:hover {
  background: #581C87;
}

.button-icon {
  font-size: 18px;
}
</style> 